"use client";

import { useMemo } from "react";
import { useZStore } from "@/utils/z-store";
import {
  Capability,
  WorkspaceRole,
  capabilitiesFor,
  hasCapability,
} from "@/shared/workspace-permissions";

export interface WorkspacePermissions {
  role: WorkspaceRole | null;
  isOwner: boolean;
  isFrozen: boolean;
  // Premium status of the ACTIVE workspace, owner-derived (§WS1). This is the
  // single client signal for gating workspace-scoped premium FEATURES — use it
  // instead of the logged-in user's own subscription, so members of a Premium
  // workspace get Premium even on a Basic plan.
  isPremium: boolean;
  planType: string | null;
  can: (capability: Capability) => boolean;
  capabilities: Capability[];
}

/**
 * Role-aware permissions for the active workspace, computed client-side from
 * the SAME shared capability map the server enforces. UI hiding mirrors — never
 * replaces — the server checks.
 */
export function useWorkspacePermissions(): WorkspacePermissions {
  const { data } = useZStore();
  const active =
    (data.workspaces || []).find((w) => w.id === data.activeWorkspaceId) ||
    null;

  return useMemo(() => {
    if (!active) {
      return {
        role: null,
        isOwner: false,
        isFrozen: false,
        isPremium: false,
        planType: null,
        can: () => false,
        capabilities: [],
      };
    }
    const subject = { role: active.role, isOwner: active.isOwner };
    return {
      role: active.role,
      isOwner: active.isOwner,
      isFrozen: active.status === "frozen",
      isPremium: !!active.isPremium,
      planType: active.planType ?? null,
      can: (capability: Capability) => hasCapability(capability, subject),
      capabilities: capabilitiesFor(subject),
    };
  }, [active]);
}
